import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';

import { getAuthenticatedUser } from '@tix/auth/state/selectors';
import {
  TixGetTicketForScannerGQL,
  TixGetTicketForScannerQuery,
  TixUpdateReservationTicketsStateGQL,
  TixUpdateTicketStateGQL
} from '@tix/data-access';
import * as moment from 'moment';
import { map, take } from 'rxjs/operators';

@Component({
  selector: 'tix-check-ticket',
  templateUrl: './check-ticket.component.html',
  styleUrls: ['./check-ticket.component.scss']
})
export class TixCheckTicketPageComponent implements OnInit {
  loadingTicket = false;
  loadingReservation = false;

  updatingTicket = false;
  updatingReservation = false;

  notFound = false;

  ticketData: TixGetTicketForScannerQuery;

  userId$ = this.store.select(getAuthenticatedUser).pipe(
    take(1),
    map(u => u?.uid)
  );

  ngOnInit(): void {
    this.route.queryParams.subscribe(queryParams => {
      this.loadTicket(queryParams['ticketId'], queryParams['reservationId']);
    });
  }

  formatTime(time: string) {
    return moment(time, 'HH:mm:ss').format('hh:mm A');
  }

  get ticket() {
    return this.ticketData.Ticket[0];
  }

  get isFree() {
    return this.ticket.ticketPrice === '$0.00';
  }

  loadTicket(ticketId: string, reservationId: string) {
    this.loadingTicket = true;

    this.getTicketData
      .fetch(
        {
          reservationId: reservationId,
          ticketId: ticketId
        },
        {
          fetchPolicy: 'no-cache'
        }
      )
      .subscribe({
        next: data => {
          this.ticketData = data.data;
          if (!data.data.Ticket.length) {
            this.notFound = true;
          }
          this.loadingTicket = false;
        },
        error: error => {
          console.error(error);
          this.notFound = true;
          this.loadingTicket = false;
        }
      });
  }

  formatCheckedInAt(time: string) {
    if (!time) return '';
    return moment(time).format('MM/DD/YYYY hh:mm A');
  }

  async handleUpdateTicket() {
    this.updatingTicket = true;

    const userId = await this.userId$.toPromise();

    this.updateTicket
      .mutate({
        checked_in_at: this.ticket.checkedInAt ? null : new Date(),
        state: this.ticket.checkedInAt ? 'Pending' : 'Checked In',
        ticketId: this.ticket.ticketId,
        updatedBy: userId || undefined
      })
      .subscribe({
        next: () => {
          this.snackbar.open(
            this.ticket.checkedInAt
              ? 'The ticket has been checked out successfully'
              : 'The ticket has been checked in successfully',
            'Close',
            {
              duration: 3000
            }
          );
          this.loadTicket(this.ticket.ticketId, this.ticket.reservationId);

          this.updatingTicket = false;
        },
        error: error => {
          console.error(error);
          this.snackbar.open('An error has ocurred', 'Close', {
            duration: 3000
          });

          this.updatingTicket = false;
        }
      });
  }

  async handleUpdateReservation() {
    this.updatingReservation = true;

    const userId = await this.userId$.toPromise();

    this.updateReservationsTicketsState
      .mutate({
        checked_in_at: this.ticket.checkedInAt ? null : new Date(),
        state: this.ticket.checkedInAt ? 'Pending' : 'Checked In',
        reservationId: this.ticket.reservationId,
        updatedBy: userId
      })
      .subscribe({
        next: () => {
          this.snackbar.open(
            this.ticket.checkedInAt
              ? 'The reservation has been checked out successfully'
              : 'The reservation has been checked in successfully',
            'Close',
            {
              duration: 3000
            }
          );
          this.loadTicket(this.ticket.ticketId, this.ticket.reservationId);

          this.updatingReservation = false;
        },
        error: error => {
          console.error(error);
          this.snackbar.open('An error has ocurred', 'Close', {
            duration: 3000
          });

          this.updatingReservation = false;
        }
      });
  }

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private getTicketData: TixGetTicketForScannerGQL,
    private updateTicket: TixUpdateTicketStateGQL,
    private updateReservationsTicketsState: TixUpdateReservationTicketsStateGQL,
    private snackbar: MatSnackBar
  ) {}
}
